import React, { useEffect } from 'react';
import { X, FileText, Shield, Database, AlertTriangle, Eye, Cookie, ExternalLink } from 'lucide-react';

export type LegalModalType = 'terms' | 'privacy' | 'disclaimer';

interface LegalModalProps {
  isOpen: boolean;
  type: LegalModalType;
  onClose: () => void;
}

// ── Terms of Use content ───────────────────────────────────────────────────────

function TermsContent() {
  return (
    <>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-base font-semibold text-amber-800 mb-2">Data Disclaimer</h3>
            <p className="text-sm text-amber-700">
              Fuel price data is obtained from{' '}
              <a href="https://www.gov.uk/guidance/access-fuel-price-data" target="_blank" rel="noopener noreferrer"
                className="underline hover:text-amber-600">gov.uk/guidance/access-fuel-price-data</a>{' '}
              and various retailer APIs. We are not responsible for any errors or inaccuracies. This service should not
              be relied upon for critical decisions.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-5 text-sm text-gray-700">
        <p>By accessing or using FuelHunt, you agree to these Terms of Use.</p>

        {[
          ['Purpose', 'FuelHunt provides fuel price information for general informational purposes only.'],
          ['No Guarantee', 'We make no warranties about the accuracy, completeness, or reliability of any data or content provided.'],
          ['User Responsibility', 'You are responsible for verifying information independently before relying on it.'],
          ['Limitation of Liability', 'FuelHunt and its developers are not liable for any loss, damage, or inconvenience resulting from use of this site or reliance on its information.'],
          ['Changes', 'We may update or modify these Terms at any time without prior notice. Continued use of the site indicates acceptance of any changes.'],
        ].map(([title, body]) => (
          <section key={title}>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">{title}</h3>
            <p>{body}</p>
          </section>
        ))}
      </div>
    </>
  );
}

// ── Privacy Policy content ─────────────────────────────────────────────────────

function PrivacyContent() {
  return (
    <>
      <div className="bg-amber-50 border border-amber-100 rounded-xl p-5 mb-6">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            FuelHunt respects your privacy and collects only the minimum information needed to operate and improve the site.
          </p>
        </div>
      </div>

      <div className="space-y-5 text-sm text-gray-700">
        {[
          {
            icon: <Database className="w-4 h-4 text-gray-400" />,
            title: 'Analytics',
            body: 'We may use analytics tools (such as Google Analytics) to collect anonymous usage statistics. This helps us understand and improve how users interact with the site.',
          },
          {
            icon: <Cookie className="w-4 h-4 text-gray-400" />,
            title: 'Cookies',
            body: 'Cookies may be used for essential site functionality or analytics — e.g. to remember your vehicle configuration and search preferences.',
          },
          {
            icon: <Shield className="w-4 h-4 text-gray-400" />,
            title: 'Personal Data',
            body: 'FuelHunt does not collect personally identifiable information unless explicitly provided. No accounts or registration are required.',
          },
          {
            icon: <ExternalLink className="w-4 h-4 text-gray-400" />,
            title: 'Data Sharing',
            body: 'We do not sell or share your data with third parties, except where required by law or necessary for core services (hosting, analytics).',
          },
        ].map(({ icon, title, body }) => (
          <section key={title}>
            <div className="flex items-center gap-2 mb-1">
              {icon}
              <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            </div>
            <p className="ml-6">{body}</p>
          </section>
        ))}

        <section>
          <div className="flex items-center gap-2 mb-1">
            <Eye className="w-4 h-4 text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-900">Location Data</h3>
          </div>
          <p className="ml-6">
            FuelHunt may request your device location to find nearby stations. This data is only used locally,
            not stored on our servers, and only accessed with your explicit permission.
          </p>
        </section>
      </div>
    </>
  );
}

// ── Data Disclaimer content ────────────────────────────────────────────────────

function DisclaimerContent() {
  return (
    <>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-700">
            This disclaimer outlines the terms and limitations regarding the fuel price data provided by FuelHunt.
          </p>
        </div>
      </div>

      <div className="space-y-5 text-sm text-gray-700">
        <section>
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Data Sources</h3>
          <p>
            Fuel price data is sourced from publicly available data provided by the UK Government{' '}
            <a href="https://www.gov.uk/guidance/access-fuel-price-data" target="_blank" rel="noopener noreferrer"
              className="text-amber-600 hover:text-amber-700 underline inline-flex items-center gap-0.5">
              gov.uk/guidance/access-fuel-price-data <ExternalLink className="w-3 h-3" />
            </a>{' '}
            and retailer APIs.
          </p>
        </section>

        {[
          ['Data Accuracy', 'FuelHunt provides data "as is" without guarantees of completeness or timeliness. Prices can change frequently — always verify directly with the fuel station before purchasing.'],
          ['Intended Use', 'This service is for informational purposes only and should not be relied upon for financial or operational decisions.'],
          ['Limitation of Liability', 'FuelHunt and its developers accept no liability for any loss, damage, or inconvenience arising from use of, or reliance on, this data or website.'],
          ['Third-Party Data', 'Where data is sourced from third-party retailers or government databases, FuelHunt acts as an aggregator and cannot guarantee the accuracy of those sources.'],
        ].map(([title, body]) => (
          <section key={title}>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">{title}</h3>
            <p>{body}</p>
          </section>
        ))}
      </div>
    </>
  );
}

// ── Modal shell ────────────────────────────────────────────────────────────────

const TITLES: Record<LegalModalType, string> = {
  terms: 'Terms of Use',
  privacy: 'Privacy Policy',
  disclaimer: 'Data Disclaimer',
};

const ICONS: Record<LegalModalType, React.ReactNode> = {
  terms: <FileText className="w-4 h-4 text-white" />,
  privacy: <Shield className="w-4 h-4 text-white" />,
  disclaimer: <Database className="w-4 h-4 text-white" />,
};

export const LegalModal: React.FC<LegalModalProps> = ({ isOpen, type, onClose }) => {
  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[82vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center shadow-sm">
              {ICONS[type]}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{TITLES[type]}</h2>
              <p className="text-sm text-gray-500">FuelHunt</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="p-6 overflow-y-auto flex-1">
          {type === 'terms'      && <TermsContent />}
          {type === 'privacy'    && <PrivacyContent />}
          {type === 'disclaimer' && <DisclaimerContent />}

          <p className="text-xs text-gray-400 mt-6 pt-4 border-t border-gray-100">
            For questions contact{' '}
            <a href="mailto:fuelhunt@hotmail.com" className="text-amber-600 hover:text-amber-700">
              fuelhunt@hotmail.com
            </a>
            {' '}·{' '}
            Last updated: {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex justify-end flex-shrink-0">
          <button onClick={onClose} className="btn-primary">Close</button>
        </div>
      </div>
    </div>
  );
};

export default LegalModal;
